import MyContext from '@/shared/contexts/AppContext';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CarregarPermissoes, ValidarPermissao } from '@/modules/system/auth/application/auth.service';
import { RoleEnum } from '@/modules/system/auth/domain/enums/role.enum';
import { PermissionEnum } from '@/modules/system/auth/domain/enums/permission.enum';
import { listPendingAttendanceSheets } from '../../../attendance-sheet.service';
import { ListAllIntershipsOfTheMonthResponseDto, AttendanceSheetResponseDto } from '@/api.types';
import { ListAllIntershipsOfTheMonthFilterDto } from '@/api.types.extra';
import { IOpcao } from '@/shared/types/selectOption';
import { useDataTableStateStates } from '@/shared/components/tables/useDataTableState';
import { readEstagiarioForAttendanceSheetSelect } from '@/modules/people/estagiario.service';
import { readPersonResponsibleSelect } from '@/modules/people/person.service';
import { useNavigationView } from '@/shared/hooks/useNavigationView';
import {
  podeExibirFiltroResponsavel,
  ehPerfilColaborador,
  filtrarResponsaveisParaPessoa,
} from '../perMonth/perMonthFilters.utils';
import {
  AttendanceSheetStatusEnum,
  getAttendanceSheetStatusEnumOptions,
} from '../../../enums/attendance-sheet-status.enum';
import {
  applyPendenciasFiltersToSearchParams,
  PendenciasListFilters,
  readPendenciasFiltersFromSearchParams,
  searchParamsHavePendenciasFilters,
} from './pendenciasFilters.utils';

export function useAttendanceSheetsPendencias(
  tableState: useDataTableStateStates,
  filtrosIniciais: PendenciasListFilters,
) {
  const context = useContext(MyContext);
  if (!context)
    throw new Error('Contexto deve ser usado dentro de um Provedor.');

  const { authVisao } = context;
  const { effectiveView, viewReady } = useNavigationView();
  const exibirFiltroResponsavel = useMemo(
    () => podeExibirFiltroResponsavel(authVisao, effectiveView),
    [authVisao, effectiveView],
  );
  const restringirResponsavelAoProprio = useMemo(
    () => ehPerfilColaborador(authVisao, effectiveView),
    [authVisao, effectiveView],
  );
  const pessoaIdSessao = context.state.pessoaId;
  const [searchParams, setSearchParams] = useSearchParams();

  const permissoes = useMemo(() => CarregarPermissoes(), []);
  const permissaoExcluirFolhas = ValidarPermissao(permissoes, [
    { role: RoleEnum.FOLHA, level: PermissionEnum.DELETE },
  ]);

  const [loaded, setLoaded] = useState(false);
  const [statusBusca, setStatusBusca] = useState<number>(filtrosIniciais.statusBusca);
  const [filtroResponsavel, setFiltroResponsavel] = useState<string | undefined>(
    filtrosIniciais.filtroResponsavel,
  );
  const [filtroEstagiario, setFiltroEstagiario] = useState<number | undefined>(
    filtrosIniciais.filtroEstagiario,
  );
  const [filtrosStatus, setFiltrosStatus] = useState<IOpcao[]>([]);
  const [responsaveis, setResponsaveis] = useState<IOpcao[]>([]);
  const [estagiarios, setEstagiarios] = useState<IOpcao[]>([]);
  const [registros, setRegistros] = useState<[ListAllIntershipsOfTheMonthResponseDto[], number]>();

  const [selectedItem, setSelectedItem] = useState<ListAllIntershipsOfTheMonthResponseDto>();
  const [folhaSelecionada, setFolhaSelecionada] = useState<ListAllIntershipsOfTheMonthResponseDto>();
  const [showModalCriarFolha, setShowModalCriarFolha] = useState(false);
  const [showModalCriarFolhas, setShowModalCriarFolhas] = useState(false);
  const [showModalNotificacaoIndividual, setShowModalNotificacaoIndividual] = useState(false);
  const [showModalNotificacoes, setShowModalNotificacoes] = useState(false);
  const [showModalResponsavel, setShowModalResponsavel] = useState(false);
  const [showModalAssinaturas, setShowModalAssinaturas] = useState(false);
  const [folhaParaAssinaturas, setFolhaParaAssinaturas] = useState<AttendanceSheetResponseDto | null>(null);
  const ignorarProximaQuery = useRef(false);

  useEffect(() => {
    if (!viewReady) return;
    if (!exibirFiltroResponsavel) {
      setResponsaveis([]);
      setFiltroResponsavel(undefined);
    }
  }, [viewReady, exibirFiltroResponsavel]);

  useEffect(() => {
    if (!restringirResponsavelAoProprio || !filtroResponsavel || !pessoaIdSessao) return;
    if (filtroResponsavel !== pessoaIdSessao) {
      setFiltroResponsavel(undefined);
    }
  }, [restringirResponsavelAoProprio, filtroResponsavel, pessoaIdSessao]);

  useEffect(() => {
    const filtros: PendenciasListFilters = {
      statusBusca,
      filtroEstagiario,
      filtroResponsavel,
      orderBy: tableState.sortConfig.key,
      orderDir: tableState.sortConfig.direction,
      page: tableState.currentPage,
      pageSize: tableState.pageSize,
    };
    setSearchParams(
      (prev) => {
        const next = applyPendenciasFiltersToSearchParams(prev, filtros);
        next.set('page', String(tableState.currentPage));
        if (next.toString() === prev.toString()) {
          ignorarProximaQuery.current = false;
          return prev;
        }
        ignorarProximaQuery.current = true;
        return next;
      },
      { replace: true },
    );
  }, [
    statusBusca,
    filtroEstagiario,
    filtroResponsavel,
    tableState.currentPage,
    tableState.pageSize,
    tableState.sortConfig.key,
    tableState.sortConfig.direction,
    setSearchParams,
  ]);

  useEffect(() => {
    if (ignorarProximaQuery.current) {
      ignorarProximaQuery.current = false;
      return;
    }

    const temParametros = searchParamsHavePendenciasFilters(searchParams)
      || searchParams.has('orderBy')
      || searchParams.has('orderDir')
      || searchParams.has('pageSize')
      || searchParams.has('page');
    if (!temParametros) return;

    const fromUrl = readPendenciasFiltersFromSearchParams(searchParams);
    if (searchParamsHavePendenciasFilters(searchParams)) {
      if (fromUrl.statusBusca !== statusBusca) setStatusBusca(fromUrl.statusBusca);
      if (fromUrl.filtroEstagiario !== filtroEstagiario) setFiltroEstagiario(fromUrl.filtroEstagiario);
      if ((fromUrl.filtroResponsavel ?? undefined) !== filtroResponsavel) {
        setFiltroResponsavel(fromUrl.filtroResponsavel);
      }
    }

    if (searchParams.has('orderBy') || searchParams.has('orderDir')) {
      const key = fromUrl.orderBy ?? 'referencia';
      const direction = fromUrl.orderDir ?? 'DESC';
      if (tableState.sortConfig.key !== key || tableState.sortConfig.direction !== direction) {
        tableState.setSortConfig({ key, direction });
      }
    }

    if (fromUrl.pageSize && searchParams.has('pageSize') && fromUrl.pageSize !== tableState.pageSize) {
      tableState.setPageSize(fromUrl.pageSize);
    }

    const page = Number(searchParams.get('page'));
    if (page > 0 && page !== tableState.currentPage) {
      tableState.setCurrentPage(page);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!viewReady && context.state.username) return;

    const carregarResponsaveis =
      exibirFiltroResponsavel && context.state.username
        ? readPersonResponsibleSelect()
        : Promise.resolve(null);

    Promise.all([readEstagiarioForAttendanceSheetSelect(), carregarResponsaveis])
      .then((response) => {
        const listaEstagiarios = response[0][0].map(
          (e) => ({ label: e.pessoa.nome, value: e.id }) as IOpcao,
        );
        listaEstagiarios.unshift({ label: '-', value: 0 });
        setEstagiarios(listaEstagiarios);

        if (response[1]) {
          const lista = response[1][0].map(
            (e) => ({ label: e.nome, value: e.id }) as unknown as IOpcao,
          );
          const restrita = (restringirResponsavelAoProprio
            ? filtrarResponsaveisParaPessoa(lista, pessoaIdSessao)
            : lista) as unknown as IOpcao[];
          const opcoesResponsaveis = [...restrita];
          opcoesResponsaveis.unshift({ label: '-', value: 0 });
          setResponsaveis(opcoesResponsaveis);
        } else if (!exibirFiltroResponsavel) {
          setResponsaveis([]);
        }
      })
      .catch((res) => toast.error(String(res)))
      .finally(() => setLoaded(true));
  }, [
    viewReady,
    exibirFiltroResponsavel,
    restringirResponsavelAoProprio,
    pessoaIdSessao,
    context.state.username,
  ]);

  const sortSignature = `${tableState.sortConfig.key}:${tableState.sortConfig.direction}`;
  const sortSignatureRef = useRef(sortSignature);
  if (sortSignatureRef.current !== sortSignature) {
    sortSignatureRef.current = sortSignature;
    if (tableState.currentPage !== 1) {
      tableState.setCurrentPage(1);
    }
  }

  const handleBuscar = () => {
    const dto: ListAllIntershipsOfTheMonthFilterDto & { orderBy?: string; orderDir?: string } = {
      unidade: 0,
      status: statusBusca,
      vinculo: 0,
      skip: (tableState.currentPage - 1) * tableState.pageSize,
      take: tableState.pageSize,
      estagiarioId: filtroEstagiario,
      responsavelId: filtroResponsavel,
      orderBy: tableState.sortConfig.key,
      orderDir: tableState.sortConfig.direction,
    };

    const dtoSumario: ListAllIntershipsOfTheMonthFilterDto = {
      ...dto,
      skip: 0,
      take: 99999,
      status: 0,
    };

    Promise.all([
      listPendingAttendanceSheets(dto),
      listPendingAttendanceSheets(dtoSumario),
    ])
      .then(([resultadoConsulta, sumario]) => {
        setRegistros(resultadoConsulta);

        const tStatus = getAttendanceSheetStatusEnumOptions().map((item) => {
          const qtd = sumario[0].filter((a) => a.folha?.status === item.value).length;
          return {
            value: Number(item.value),
            label: `${item.label} ${qtd > 0 ? `(${qtd})` : ''}`,
          };
        });

        const qtdExcetoConcluidos = sumario[0].filter(
          (a) => a.folha?.status !== AttendanceSheetStatusEnum.CONCLUIDA,
        ).length;
        tStatus.unshift({
          value: -1,
          label: `Exceto concluídos ${qtdExcetoConcluidos > 0 ? `(${qtdExcetoConcluidos})` : ''}`,
        });
        setFiltrosStatus(tStatus);
      })
      .catch((e) => toast.error(String(e)));
  };

  useEffect(() => {
    if (!loaded) return;
    handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loaded,
    statusBusca,
    filtroResponsavel,
    filtroEstagiario,
    tableState.currentPage,
    tableState.pageSize,
    tableState.sortConfig.key,
    tableState.sortConfig.direction,
  ]);

  return {
    statusBusca,
    setStatusBusca,
    filtroResponsavel,
    setFiltroResponsavel,
    filtroEstagiario,
    setFiltroEstagiario,
    filtrosStatus,
    registros,
    handleBuscar,
    responsaveis,
    estagiarios,
    exibirFiltroResponsavel,
    permissaoExcluirFolhas,
    selectedItem,
    setSelectedItem,
    folhaSelecionada,
    setFolhaSelecionada,
    showModalCriarFolha,
    setShowModalCriarFolha,
    showModalCriarFolhas,
    setShowModalCriarFolhas,
    showModalNotificacaoIndividual,
    setShowModalNotificacaoIndividual,
    showModalNotificacoes,
    setShowModalNotificacoes,
    showModalResponsavel,
    setShowModalResponsavel,
    showModalAssinaturas,
    setShowModalAssinaturas,
    folhaParaAssinaturas,
    setFolhaParaAssinaturas,
    loaded,
  };
}
